import { useState } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow
} from '@mui/material';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { verificationAPI } from '../../services/api';

const VerifyBankAccount = () => {
  const [formData, setFormData] = useState({
    accountNumber: '',
    ifsc: '',
    name: ''
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (loading) {
      // console.log('⚠️ Request already in progress, ignoring duplicate click');
      return;
    }
    
    setLoading(true);
    setError('');
    setResult(null);

    try {
      const response = await verificationAPI.verifyBankAccount(
        formData.accountNumber,
        formData.ifsc.toUpperCase(),
        formData.name
      );
      // console.log('✅ Response received from server:', response);
      
      // Parse ifsc_details if it's a string
      if (response.data && typeof response.data.ifsc_details === 'string') {
        try {
          response.data.ifsc_details = JSON.parse(response.data.ifsc_details);
        } catch (e) {
          // console.error('Failed to parse ifsc_details:', e);
        }
      }
      
      setResult(response);
      toast.success('Bank account verification successful!');
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Verification failed';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 2, sm: 3 } }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}>
          🏦 Bank Account Verification
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: { xs: 3, sm: 4 } }}>
          Verify bank account details with ownership confirmation
        </Typography>

        <Paper sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Number"
                  name="accountNumber"
                  value={formData.accountNumber}
                  onChange={handleChange}
                  placeholder="Enter account number"
                  required
                  inputProps={{
                    pattern: '[0-9]+',
                    title: 'Account number should contain only digits'
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  name="ifsc"
                  value={formData.ifsc}
                  onChange={handleChange}
                  placeholder="ABCD0123456"
                  inputProps={{
                    maxLength: 11,
                    pattern: '[A-Za-z]{4}0[A-Za-z0-9]{6}',
                    title: 'IFSC format: 4 letters, 0, 6 alphanumeric'
                  }}
                  helperText="Format: 4 letters, 0, 6 alphanumeric characters (e.g., SBIN0001234)"
                  required
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Account Holder Name (Optional)"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Enter account holder name for name matching"
                  helperText="Providing name will enable name matching verification"
                />
              </Grid>

              {error && (
                <Grid item xs={12}>
                  <Alert severity="error">{error}</Alert>
                </Grid>
              )}

              <Grid item xs={12}>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  size="large"
                  disabled={loading}
                  sx={{ height: { xs: '48px', sm: '56px' } }}
                >
                  {loading ? 'Verifying...' : 'Verify Bank Account'}
                </Button>
              </Grid>
            </Grid>
          </Box>

          {result && (
            <Paper sx={{ mt: { xs: 2, sm: 3 }, p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5' }}>
              <Typography variant="h6" gutterBottom>
                Verification Result
              </Typography>
              <Alert severity={result.success ? 'success' : 'error'} sx={{ mb: 2 }}>
                {result.success ? '✓ Verification Successful' : '✗ Verification Failed'}
              </Alert>
              
              {result.data && (
                <TableContainer component={Paper} sx={{ mb: 2 }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', width: '40%' }}>
                          Account Number
                        </TableCell>
                        <TableCell>{result.data.account_number || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          Name at Bank
                        </TableCell>
                        <TableCell>{result.data.name_at_bank || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          Bank Name
                        </TableCell>
                        <TableCell>{result.data.bank_name || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          IFSC Code
                        </TableCell>
                        <TableCell>{result.data.ifsc || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          Branch
                        </TableCell>
                        <TableCell>{result.data.branch || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          City
                        </TableCell>
                        <TableCell>{result.data.city || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          MICR Code
                        </TableCell>
                        <TableCell>{result.data.micr || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          Account Status
                        </TableCell>
                        <TableCell>
                          <Box sx={{ 
                            display: 'inline-block', 
                            px: 2, 
                            py: 0.5, 
                            borderRadius: 1,
                            bgcolor: result.data.account_status === 'VALID' ? '#4caf50' : '#f44336',
                            color: 'white',
                            fontWeight: 600
                          }}>
                            {result.data.account_status || '–'}
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          Account Status Code
                        </TableCell>
                        <TableCell>{result.data.account_status_code || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          Name Match Result
                        </TableCell>
                        <TableCell>{result.data.name_match_result || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          Name Match Score
                        </TableCell>
                        <TableCell>{result.data.name_match_score || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          UTR
                        </TableCell>
                        <TableCell>{result.data.utr || '–'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5' }}>
                          Reference ID
                        </TableCell>
                        <TableCell>{result.data.reference_id || '–'}</TableCell>
                      </TableRow>
                      {result.data.ifsc_details && (
                        <>
                          <TableRow>
                            <TableCell colSpan={2} sx={{ fontWeight: 700, bgcolor: '#e0e0e0', py: 1.5 }}>
                              IFSC Details
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>
                              Bank
                            </TableCell>
                            <TableCell>{result.data.ifsc_details.bank || '–'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>
                              Branch
                            </TableCell>
                            <TableCell>{result.data.ifsc_details.branch || '–'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>
                              Address
                            </TableCell>
                            <TableCell>{result.data.ifsc_details.address || '–'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>
                              City
                            </TableCell>
                            <TableCell>{result.data.ifsc_details.city || '–'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>
                              State
                            </TableCell>
                            <TableCell>{result.data.ifsc_details.state || '–'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>
                              IFSC Subcode
                            </TableCell>
                            <TableCell>{result.data.ifsc_details.ifsc_subcode || '–'}</TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 600, bgcolor: '#f5f5f5', pl: 4 }}>
                              SWIFT Code
                            </TableCell>
                            <TableCell>{result.data.ifsc_details.swift_code || '–'}</TableCell>
                          </TableRow>
                        </>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              
              <Typography variant="body2" color="text.secondary">
                Credits Deducted: ₹{result.creditsDeducted || 0}
              </Typography>
            </Paper>
          )}
        </Paper>
      </Container>
    </Layout>
  );
};

export default VerifyBankAccount;
